/**
 * Zod Schemas for Home Showcases API
 *
 * Centralized schemas for /api/search/home-showcases endpoint
 */

import { z } from 'zod';
import { decodeAffinityParam } from '../../../utils/affinity-param';

// =============================================================================
// SHARED SCHEMAS
// =============================================================================

// User photo object - matches format from getUserPhotoUrls
export const UserPhotoSchema = z.object({
  url: z.string(),
  thumbUrl: z.string(),
}).nullable();

// User object in listing response
export const ListingUserSchema = z.object({
  id: z.union([z.string(), z.number()]),
  firstName: z.string(),
  lastName: z.string(),
  trustLevel: z.number(),
  photos: UserPhotoSchema,
});

export type ListingUser = z.infer<typeof ListingUserSchema>;

// Photo object in listing response
export const ListingPhotoSchema = z.object({
  url: z.string(),
  thumbUrl: z.string(),
  '320x320Url': z.string(),
});

export type ListingPhoto = z.infer<typeof ListingPhotoSchema>;

// Listing object in search response
export const SearchListingSchema = z.object({
  id: z.string(),
  title: z.string(),
  text: z.string().nullish(),
  price: z.number(),
  cost: z.number(),
  original_price: z.number().nullish(),
  slashedPrice: z.string().nullish(),
  percentOff: z.number().nullish(),
  categoryId: z.number(),
  categoryGroupId: z.number(),
  categoryName: z.string().optional(),
  itemCondition: z.number().nullish(),
  status: z.number(),
  shippable: z.union([z.number(), z.boolean()]),
  locationText: z.string().nullish(),
  createdDate: z.string().nullish(),
  featuredDate: z.string().nullish(),
  tags: z.array(z.string()).optional().default([]),
  dataSourceId: z.number().nullish(),
  remote_url: z.string().nullish(),
  hasPhoto: z.number(),
  photo: z.string().nullish(),
  photos: z.array(ListingPhotoSchema),
  user: ListingUserSchema.nullish(),
  distance: z.number().optional(),
  /** This seller's live listings of the same item, this one included (utils/listing-copies.ts). */
  copies: z.number().int().positive().optional(),
});

export type SearchListing = z.infer<typeof SearchListingSchema>;

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

export const HomeShowcasesRequestSchema = z.object({
  minProductsPerGroup: z.string().optional().default('7'),
  categoryGroups: z.string().optional().default('12,15,26,34,23,19,24,17,25,35,20'),
  /**
   * JSON array of [tag, weight] pairs -- the visitor's in-session affinity
   * vector. Decoded defensively: anything malformed becomes an empty vector,
   * which the relevance script treats as neutral. See utils/affinity-param.ts.
   */
  affinity: z.string().optional().transform(raw => decodeAffinityParam(raw)),
  /**
   * The visitor's persistent `anon_id`. It picks the ranking-experiment bucket,
   * and (when the `sw_anonymous_id` cookie is missing) says whose "Not
   * Interested" answers to honour. It is already on every analytics event this
   * page emits, so sending it here adds no identifier the request did not
   * already carry. Absent means the control arm.
   */
  anonymousId: z.string().max(64).optional(),
  /**
   * Groups the server deferred earlier in this visit (see `deferredGroups`) and
   * the page is now asking for again at the end of its list. Served as-is: a
   * "Show less" pushes a row to the back once, it does not push it off the page.
   */
  pinnedGroups: z
    .string()
    .optional()
    .transform(raw =>
      (raw ?? '')
        .split(',')
        .map(id => parseInt(id, 10))
        .filter(id => Number.isInteger(id) && id > 0)
    ),
  /**
   * The page's first request: `categoryGroups` is every candidate, and the
   * server ranks them (utils/home-row-rank.ts), serves rows for the top
   * `serveCount`, and returns the whole order in `groupOrder`.
   */
  rankGroups: z
    .string()
    .optional()
    .transform(raw => raw === '1' || raw === 'true'),
  serveCount: z.coerce.number().int().min(1).max(20).optional().default(6),
  /** A picked sort orders each row's listings (utils/listing-sort.ts); the row order stays ranked. */
  sortBy: z.string().max(32).optional(),
  /**
   * This session's interest per group, `id:weight,id:weight`
   * (utils/session-interest.ts). Malformed pairs are dropped.
   */
  interest: z
    .string()
    .max(2000)
    .optional()
    .transform(raw =>
      (raw ?? '')
        .split(',')
        .map(pair => pair.split(':').map(Number))
        .filter(([id, w]) => Number.isInteger(id) && id > 0 && Number.isFinite(w) && w > 0)
        .map(([categoryGroupId, weight]) => ({ categoryGroupId, weight }))
    ),
});

export type HomeShowcasesRequest = z.infer<typeof HomeShowcasesRequestSchema>;

// =============================================================================
// RESPONSE SCHEMAS
// =============================================================================

// Category group showcase item
export const CategoryGroupShowcaseSchema = z.object({
  categoryGroupId: z.number(),
  categoryGroupName: z.string(),
  categoryGroupSlug: z.string(),
  listings: z.array(SearchListingSchema),
});

export type CategoryGroupShowcase = z.infer<typeof CategoryGroupShowcaseSchema>;

// Success response
export const HomeShowcasesSuccessResponseSchema = z.object({
  success: z.literal(true),
  showcases: z.array(CategoryGroupShowcaseSchema),
  /**
   * Which ranking arm served this response: a variant id, or `control`. The
   * client stashes it and attaches it to the analytics events for the rest of
   * the visit, which is the whole mechanism by which the arms can be compared.
   */
  rankingVariant: z.string().optional(),
  total: z.number(),
  /**
   * Groups asked for but held back because the reader said "Show less" to them.
   * The page moves each one to the end of its order and asks again there, with
   * the id in `pinnedGroups`. A group they said "Show none" to is simply absent.
   */
  deferredGroups: z.array(z.number()).optional(),
  /** With `rankGroups`: every candidate group, highest score first. Later pages walk it. */
  groupOrder: z.array(z.number()).optional(),
  /** With `rankGroups`: each group's score, on the scale of HOME_SPECIAL_PRIORS. */
  groupScores: z.record(z.string(), z.number()).optional(),
});

export type HomeShowcasesSuccessResponse = z.infer<typeof HomeShowcasesSuccessResponseSchema>;

// Error response
export const HomeShowcasesErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.string(),
});

export type HomeShowcasesErrorResponse = z.infer<typeof HomeShowcasesErrorResponseSchema>;

// Combined response schema (discriminated union)
export const HomeShowcasesResponseSchema = z.union([
  HomeShowcasesSuccessResponseSchema,
  HomeShowcasesErrorResponseSchema,
]);

export type HomeShowcasesResponse = z.infer<typeof HomeShowcasesResponseSchema>;
